import React, { useCallback, useMemo } from "react";
import { getLinkData, getParams, getUrl } from "../../../../redux/lots/action";
import { removeFilter } from "../../../../redux/sidebars/filter-sidebar/actionFilterSideBar";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import queryString from "query-string";
import GlobalHelper from "../../../../helpers/global-helper";
import includes from "lodash/includes";
import find from "lodash/find";

// Константы для улучшения читаемости
const LOT_TYPES = {
    AUCTION: 'auction_lots',
    ARCHIVE: 'archive_lots'
};

const SPECIAL_CATEGORY_ID = 56;

const CategoryLinkComponent = React.memo((props) => {
    const { item, className = '' } = props;
    const { category_description, auction_category_id } = item;
    
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    // Получаем данные из Redux store
    const { user_sort_data } = useSelector((state) => state.lots);
    const linkParams = useSelector((state) => {
        try {
            return dispatch(getParams());
        } catch (error) {
            console.error('Error getting params:', error);
            return {};
        }
    });
    
    const { user_lots_type } = linkParams || {};

    // Мемоизируем параметры сортировки пользователя
    const userSortParams = useMemo(() => {
        if (!user_sort_data || !user_lots_type) {
            return { direction: null, order: null };
        }
        
        const sortData = find(user_sort_data, { user_lots_type });
        if (!sortData) {
            return { direction: null, order: null };
        }
        
        return {
            direction: sortData.direction,
            order: sortData.sort_order
        };
    }, [user_lots_type, user_sort_data]);

    // Создаем параметры ссылки
    const getLinkParams = useCallback(() => {
        if (!linkParams) return '';
        
        try {
            const params = { ...linkParams };
            delete params.auction_category_id;
            params.page = 1;
            
            const urlParams = queryString.parse(dispatch(getLinkData(params)));

            // Удаляем параметры сортировки если они совпадают с пользовательскими
            if (userSortParams.direction === urlParams.direction) {
                delete urlParams.direction;
            }
            if (userSortParams.order === urlParams.order) {
                delete urlParams.order;
            }
            
            return queryString.stringify(urlParams);
        } catch (error) {
            console.error('Error creating link params:', error);
            return '';
        }
    }, [dispatch, linkParams, userSortParams]);

    // Обработчик клика для установки параметров
    const handleSetParameters = useCallback(() => {
        try {
            // Удаляем фильтр prefix_id если он существует
            if (linkParams?.prefix_id !== undefined) {
                dispatch(removeFilter('prefix_id'));
                const queryLink = dispatch(getLinkData({ page: 1 }));
                navigate(`/?${queryLink}`);
            }

            // Специальная обработка для категории с ID 56
            if (auction_category_id === SPECIAL_CATEGORY_ID && user_lots_type !== LOT_TYPES.ARCHIVE) {
                if (item.category_link) {
                    window.location.href = item.category_link;
                }
            }
        } catch (error) {
            console.error('Error setting parameters:', error);
        }
    }, [dispatch, linkParams, navigate, auction_category_id, user_lots_type, item.category_link]);

    // Обработчик клика для обычных ссылок
    const handleLinkClick = useCallback((e) => {
        e.preventDefault();
        try {
            const queryLink = dispatch(getLinkData({ auction_category_id, page: 1 }));
            navigate(`?${queryLink}`);
        } catch (error) {
            console.error('Error navigating to link:', error);
        }
    }, [dispatch, auction_category_id, navigate]);

    // Проверяем, является ли тип лотов поддерживаемым
    const isSupportedLotType = useMemo(() => {
        return includes([LOT_TYPES.AUCTION, LOT_TYPES.ARCHIVE], user_lots_type);
    }, [user_lots_type]);

    // Рендерим Link для поддерживаемых типов лотов
    if (isSupportedLotType) {
        try {
            const { link } = dispatch(getUrl());
            const basePath = (user_lots_type === LOT_TYPES.AUCTION) ? '/auctions/' : link;

            const paramsTo = {
                pathname: `${basePath}${GlobalHelper.getCategoryVariableReference(item)}`,
                search: `?${getLinkParams()}`
            };

            return (
                <Link 
                    onClick={handleSetParameters} 
                    className={className} 
                    to={paramsTo}
                    title={category_description}
                >
                    {props.children}
                </Link>
            );
        } catch (error) {
            console.error('Error rendering Link component:', error);
            // Fallback к обычной ссылке
        }
    }

    // Рендерим обычную ссылку как fallback
    return (
        <a
            href={`/auctions/${GlobalHelper.getCategoryVariableReference(item)}`}
            onClick={handleLinkClick}
            className={className}
            title={category_description}
        >
            {props.children}
        </a>
    );
});

// Добавляем displayName для лучшей отладки
CategoryLinkComponent.displayName = 'CategoryLinkComponent';

export default CategoryLinkComponent;